import { z } from "zod";
import { SCOPE_TYPES } from "../auth/permissions";
import { emailSchema } from "../auth/schemas";

/** Admin contracts (WP-1.8): users, role assignments, legal entities, business units. */

export const USER_STATUSES = ["INVITED", "ACTIVE", "DISABLED"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];
export const LOCALES = ["it", "en"] as const;

export const displayNameSchema = z.string().trim().min(1).max(200);

// ── Role assignments ─────────────────────────────────────────────────────────

/** Input for granting a role. Scope rules per role: `checkRoleAssignment` (auth/scope.ts). */
export const roleGrantSchema = z.object({
  roleKey: z.string().trim().min(1).max(64),
  scopeType: z.enum(SCOPE_TYPES),
  scopeId: z.uuid().nullable(),
});
export type RoleGrant = z.infer<typeof roleGrantSchema>;

export const userRoleAssignmentSchema = z.object({
  /** Assignment id (used to remove it). */
  id: z.uuid(),
  roleKey: z.string(),
  roleName: z.string(),
  scopeType: z.enum(SCOPE_TYPES),
  scopeId: z.uuid().nullable(),
  /** Code + name of the legal entity / business unit; null for GLOBAL. */
  scopeLabel: z.string().nullable(),
});
export type UserRoleAssignment = z.infer<typeof userRoleAssignmentSchema>;

// ── Users ────────────────────────────────────────────────────────────────────

export const adminUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  status: z.enum(USER_STATUSES),
  provider: z.enum(["LOCAL", "ENTRA"]),
  locale: z.enum(LOCALES),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  roles: z.array(userRoleAssignmentSchema),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const userListQuerySchema = z.object({
  /** Matches email or display name (case-insensitive substring). */
  q: z.string().trim().max(100).optional(),
  status: z.enum(USER_STATUSES).optional(),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type UserListQuery = z.infer<typeof userListQuerySchema>;

export const userListResponseSchema = z.object({
  items: z.array(adminUserSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type UserListResponse = z.infer<typeof userListResponseSchema>;

/** How a new user signs in (ADR-0028): `password` → local account + invite email; `microsoft` → Entra, no email. */
export const SIGN_IN_METHODS = ["password", "microsoft"] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];

/** Creates a user: a local (email + password) user with an emailed invite, or one who signs in with Microsoft. */
export const createUserRequestSchema = z.object({
  email: emailSchema,
  displayName: displayNameSchema,
  locale: z.enum(LOCALES).default("en"),
  signIn: z.enum(SIGN_IN_METHODS).default("password"),
  roles: z.array(roleGrantSchema).min(1).max(50),
});
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

/**
 * `inviteSent: false` for a password user means the user exists but the email failed: offer "resend invite".
 * Microsoft users are never sent an invite (`inviteSent: false`, provider ENTRA).
 */
export const createUserResponseSchema = z.object({ user: adminUserSchema, inviteSent: z.boolean() });
export type CreateUserResponse = z.infer<typeof createUserResponseSchema>;

export const updateUserRequestSchema = z
  .object({ displayName: displayNameSchema.optional(), locale: z.enum(LOCALES).optional() })
  .refine((v) => v.displayName !== undefined || v.locale !== undefined, { message: "Nothing to update." });
export type UpdateUserRequest = z.infer<typeof updateUserRequestSchema>;

export const roleSummarySchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  permissions: z.array(z.string()),
  scopeTypes: z.array(z.enum(SCOPE_TYPES)),
});
export type RoleSummary = z.infer<typeof roleSummarySchema>;

// ── Organisation ─────────────────────────────────────────────────────────────

/** Short stable code, e.g. "TS-SPA" or "BU_HR". Stored upper-case. */
export const orgCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .pipe(z.string().regex(/^[A-Z0-9][A-Z0-9._-]{0,63}$/, { message: "Use letters, digits, . _ - (max 64)." }));
export const orgNameSchema = z.string().trim().min(1).max(200);

export const legalEntitySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  active: z.boolean(),
});
export type LegalEntity = z.infer<typeof legalEntitySchema>;

export const createLegalEntitySchema = z.object({ code: orgCodeSchema, name: orgNameSchema });
export type CreateLegalEntity = z.infer<typeof createLegalEntitySchema>;

export const updateLegalEntitySchema = z
  .object({ code: orgCodeSchema.optional(), name: orgNameSchema.optional(), active: z.boolean().optional() })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "Nothing to update." });
export type UpdateLegalEntity = z.infer<typeof updateLegalEntitySchema>;

export const businessUnitSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  legalEntityId: z.uuid().nullable(),
  active: z.boolean(),
});
export type BusinessUnit = z.infer<typeof businessUnitSchema>;

export const createBusinessUnitSchema = z.object({
  code: orgCodeSchema,
  name: orgNameSchema,
  legalEntityId: z.uuid().nullable().default(null),
});
export type CreateBusinessUnit = z.infer<typeof createBusinessUnitSchema>;

export const updateBusinessUnitSchema = z
  .object({
    code: orgCodeSchema.optional(),
    name: orgNameSchema.optional(),
    legalEntityId: z.uuid().nullable().optional(),
    active: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: "Nothing to update." });
export type UpdateBusinessUnit = z.infer<typeof updateBusinessUnitSchema>;

export const orgOverviewSchema = z.object({
  legalEntities: z.array(legalEntitySchema),
  businessUnits: z.array(businessUnitSchema),
});
export type OrgOverview = z.infer<typeof orgOverviewSchema>;

export const idParamSchema = z.object({ id: z.uuid() });
export const userRoleParamSchema = z.object({ id: z.uuid(), assignmentId: z.uuid() });
